// import the region map from wherever it lives
// adjust the path to your project structure
import { currencyBasedOnRegion } from '@/utils'

type TypeGAItem = {
  item_id: string
  item_name: string
  affiliation?: string
  coupon?: string
  discount?: number
  index?: number
  item_brand?: string
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_list_id?: string
  item_list_name?: string
  item_variant?: string
  location_id?: string
  price?: number
  quantity: number
}

type TypeProductItem = {
  units?: number
  brand: { name: string }
  name: string
  categories?: { name: string }[]
  variant: {
    ga_list?: { id: string; name: string }
    idx?: number
    name: string
    sku: string
    price: {
      discount?: number
      salePrice: number
      retailPrice?: number
    }
  }
}

type TypeCartData = {
  value?: number
  cartLines: TypeProductItem[]
}

/* ---------- currency helpers ---------- */

type CountryCodeLike = keyof typeof currencyBasedOnRegion | string
type CurrencyUnion = 'EUR' | 'GBP' | 'USD' | 'AUD'

const resolveCurrency = (countryCode: CountryCodeLike): CurrencyUnion => {
  const code =
    currencyBasedOnRegion[
      countryCode as keyof typeof currencyBasedOnRegion
    ]?.code ?? 'EUR'
  return code as CurrencyUnion
}

/* ---------- utils ---------- */

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100

const calcDiscount = (retail?: number, sale?: number, provided?: number): number => {
  if (typeof provided === 'number') return provided
  if (typeof retail === 'number' && typeof sale === 'number') return round2(retail - sale)
  return 0
}

/* ---------- mapping ---------- */

const getProductItem = (item: TypeProductItem): TypeGAItem => {
  const { brand, categories, name, units, variant } = item
  const quantity = units ?? 1
  const salePrice = variant.price?.salePrice ?? 0
  const discount = calcDiscount(variant.price?.retailPrice, salePrice, variant.price?.discount)

  const out: TypeGAItem = {
    item_id: variant.sku,
    item_name: name,
    item_brand: brand?.name ?? 'Packs',
    item_variant: variant.name,
    price: salePrice,
    quantity
  }

  if (discount) out.discount = discount
  if (typeof variant.idx === 'number') out.index = variant.idx

  const catKeys = [
    'item_category',
    'item_category2',
    'item_category3',
    'item_category4',
    'item_category5'
  ] as const

  categories?.forEach((cat, i) => {
    const key = catKeys[i]
    if (key) out[key] = cat.name
  })

  if (variant.ga_list) {
    out.item_list_id = variant.ga_list.id
    out.item_list_name = variant.ga_list.name
  }

  return out
}

/* ---------- payload builders ---------- */

type CartEvent = 'add_to_cart' | 'remove_from_cart' | 'view_cart'
type ItemEvent = 'view_item' | 'select_item'
type ListEvent = 'view_item_list'

function getCartPayload<E extends CartEvent>(
  eventName: E,
  data: TypeCartData,
  currency: CurrencyUnion
): {
  event: E
  ecommerce: { currency: CurrencyUnion; value: number; items: TypeGAItem[] }
} {
  const { value, cartLines } = data

  const totalValue =
    typeof value === 'number'
      ? value
      : round2(
        cartLines.reduce((acc, line) => {
          const q = line.units ?? 1
          const price = line.variant.price?.salePrice ?? 0
          return acc + price * q
        }, 0)
      )

  const items = cartLines.map(getProductItem)

  return {
    event: eventName,
    ecommerce: {
      currency,
      value: totalValue,
      items
    }
  }
}

function getItemPayload<E extends ItemEvent>(
  eventName: E,
  data: { product: TypeProductItem }
): { event: E; ecommerce: { items: TypeGAItem[] } } {
  return {
    event: eventName,
    ecommerce: { items: [getProductItem(data.product)] }
  }
}

function getListPayload<E extends ListEvent>(
  eventName: E,
  data: { products: TypeProductItem[] }
): { event: E; ecommerce: { items: TypeGAItem[] } } {
  return {
    event: eventName,
    ecommerce: { items: data.products.map(getProductItem) }
  }
}

/* ---------- type-safe event maps ---------- */

type EventPayloadMap = {
  add_to_cart: TypeCartData
  remove_from_cart: TypeCartData
  view_cart: TypeCartData
  view_item: { product: TypeProductItem }
  view_item_list: { products: TypeProductItem[] }
  select_item: { product: TypeProductItem }
}

type EventReturnMap = {
  add_to_cart: ReturnType<typeof getCartPayload<'add_to_cart'>>
  remove_from_cart: ReturnType<typeof getCartPayload<'remove_from_cart'>>
  view_cart: ReturnType<typeof getCartPayload<'view_cart'>>
  view_item: ReturnType<typeof getItemPayload<'view_item'>>
  view_item_list: ReturnType<typeof getListPayload<'view_item_list'>>
  select_item: ReturnType<typeof getItemPayload<'select_item'>>
}

function getPayloadByEvent<E extends keyof EventPayloadMap>(
  eventName: E,
  data: EventPayloadMap[E],
  countryCode: CountryCodeLike
): EventReturnMap[E] {
  if (eventName === 'add_to_cart' || eventName === 'remove_from_cart' || eventName === 'view_cart') {
    const currency = resolveCurrency(countryCode)
    return getCartPayload(eventName, data as TypeCartData, currency) as EventReturnMap[E]
  }

  if (eventName === 'view_item_list') {
    return getListPayload('view_item_list', data as { products: TypeProductItem[] }) as EventReturnMap[E]
  }

  return getItemPayload(eventName as ItemEvent, data as { product: TypeProductItem }) as EventReturnMap[E]
}

/* ---------- public API ---------- */

export function pushAnalyticsEvent<E extends keyof EventPayloadMap>(
  eventName: E,
  data: EventPayloadMap[E],
  countryCode: CountryCodeLike
): void {
  if (typeof window === 'undefined') return

  type GTMDataLayer = Array<Record<string, unknown>>
  const w = window as unknown as { dataLayer?: GTMDataLayer }

  w.dataLayer = w.dataLayer ?? []
  w.dataLayer.push({ ecommerce: null }) // GA4 reset
  w.dataLayer.push(getPayloadByEvent(eventName, data, countryCode))
}
